import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { MONTHS_IT, fmtHours, fmtHoursSigned, lastCompletedMonth } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, AlertTriangle, Activity, Sparkles, Palmtree, HeartPulse, CheckCircle2, ArrowRight, KeyRound, Lock, ClipboardList, Bell, Import as ImportIcon, CalendarCheck, Home, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useNavigate } from "react-router-dom";
import { SuiteKpi, SuiteCard, SuiteButton, SuiteEyebrow, SuiteBadge, SuiteTable, SuiteTh, SuiteTd, SuiteBalanceCell, SuiteMonthPicker } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { announceShellCompany, companyNameForShell } from "@/lib/shellCompany";
import { formatDateIT, formatDateTimeIT } from "@/foundation/formatting";
import { useEffectiveRole } from "@/foundation/context";

function ActivityIcon({ kind }) {
  const map = {
    password_reset:      { Icon: KeyRound,      color: "bg-violet-100 text-violet-700" },
    month_close:         { Icon: Lock,          color: "bg-suite-neutral-100 text-suite-neutral-700" },
    attendance_sent:     { Icon: ClipboardList, color: "bg-suite-secondary-light text-suite-secondary" },
    attendance_approved: { Icon: CheckCircle2,  color: "bg-suite-accent-light text-suite-accent-hover" },
    attendance_rejected: { Icon: AlertTriangle, color: "bg-suite-error-light text-suite-error" },
  };
  const { Icon, color } = map[kind] || map.attendance_sent;
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color} flex-shrink-0`}>
      <Icon size={15} strokeWidth={2} />
    </div>
  );
}

function ActivityItem({ ev, onClick }) {
  const fmt = formatDateTimeIT(ev.at, "—");
  return (
    <li
      onClick={() => onClick && onClick(ev)}
      className={`p-3 flex items-start gap-3 hover:bg-suite-surface-hover transition-colors ${onClick ? "cursor-pointer" : ""}`}
      data-testid={`activity-${ev.id}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <ActivityIcon kind={ev.kind} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-suite-neutral-700 font-medium truncate">{ev.title}</div>
        <div className="text-xs text-suite-neutral-500 truncate">
          {ev.detail}
          {ev.company_name && <span className="text-suite-neutral-400"> · {ev.company_name}</span>}
        </div>
      </div>
      <div className="text-[10px] font-mono text-suite-neutral-400 whitespace-nowrap flex-shrink-0 pt-1">{fmt}</div>
    </li>
  );
}


function Stat() { return null; /* deprecated — sostituito da SuiteKpi */ }
void Stat;



export default function Dashboard() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const _defMonth = lastCompletedMonth();
  const [year, setYear] = useState(_defMonth.year);
  const [month, setMonth] = useState(_defMonth.month);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excSummary, setExcSummary] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [focusFilter, setFocusFilter] = useState(null);
  const [kpiDetail, setKpiDetail] = useState(null);

  // Costruisce la lista di dipendenti da mostrare nel dialog Focus (filtrata)
  const focusList = React.useMemo(() => {
    if (!focusFilter || !excSummary?.employees) return { title: "", rows: [] };
    const meta = {
      regular:   { title: "Dipendenti regolari (nessuna eccezione)", filter: (e) => !e.has_exc },
      with_exc:  { title: "Dipendenti con eccezioni",                filter: (e) => e.has_exc },
      ferie:     { title: "Dipendenti con ferie nel mese",           filter: (e) => (e.ferie_h || 0) > 0 },
      permessi:  { title: "Dipendenti con permessi nel mese",        filter: (e) => (e.permessi_h || 0) > 0 },
      malattia:  { title: "Dipendenti con malattia nel mese",        filter: (e) => (e.malattia_h || 0) > 0 },
    }[focusFilter] || { title: "", filter: () => false };
    return { title: meta.title, rows: excSummary.employees.filter(meta.filter) };
  }, [focusFilter, excSummary]);

  const kpiList = React.useMemo(() => {
    if (!kpiDetail || !summary) return { title: "", description: "", rows: [] };
    const configurations = {
      employees: {
        title: "Dipendenti attivi",
        description: "Organico attivo nel periodo selezionato",
        rows: (summary.active_employees || []).map((row) => ({
          ...row,
          primary: row.name,
          secondary: row.matricola ? `Matricola ${row.matricola}` : "Matricola non indicata",
        })),
      },
      pending: {
        title: "Cartellini da validare",
        description: "Cartellini mensili inviati dai dipendenti e in attesa di validazione",
        rows: (summary.attendance_pending_details || []).map((row) => ({
          ...row,
          primary: row.name,
          secondary: row.submitted_at
            ? `Inviato dal dipendente il ${formatDateTimeIT(row.submitted_at)}`
            : "Inviato dal dipendente",
        })),
      },
      balance: {
        title: "Saldo banca ore",
        description: "Progressivo per dipendente nel periodo selezionato",
        rows: (summary.per_employee || []).filter((row) => row.bank_hours_enabled).map((row) => ({
          ...row,
          primary: row.name,
          secondary: `Mese ${fmtHoursSigned(row.month_balance)} · Progressivo ${fmtHoursSigned(row.progressive_balance)}`,
        })),
      },
      anomalies: {
        title: "Anomalie da controllare",
        description: "Dettaglio delle anomalie comprese nel numero mostrato",
        rows: (summary.anomalies || []).map((row) => ({
          ...row,
          primary: row.message,
          secondary: row.type === "missing_attendance" ? "Presenze mancanti" : "Saldo da verificare",
        })),
      },
      imports: {
        title: "Import del mese",
        description: "File acquisiti nel periodo selezionato",
        rows: (summary.imports_detail || []).map((row) => ({
          ...row,
          primary: row.filename || "Import cartellino",
          secondary: row.created_at ? `Caricato il ${formatDateTimeIT(row.created_at)}` : "Data non disponibile",
        })),
      },
      closed: {
        title: `Mesi chiusi nel ${year}`,
        description: "Mesi con almeno un cartellino concluso nel workflow",
        rows: (summary.closed_months_detail || []).map((row) => ({
          ...row,
          primary: `${MONTHS_IT[row.month - 1]} ${row.year}`,
          secondary: `${row.employees_count} dipendenti con mese chiuso`,
        })),
      },
    };
    return configurations[kpiDetail] || { title: "", description: "", rows: [] };
  }, [kpiDetail, summary, year]);

  const openKpiRow = (row) => {
    const detailType = kpiDetail;
    setKpiDetail(null);
    if (detailType === "imports") {
      navigate("/import-cartellino");
    } else if (detailType === "closed") {
      navigate(`/presenze?year=${row.year}&month=${row.month}`);
    } else if (row.employee_id) {
      navigate(`/presenze?employee=${row.employee_id}&year=${year}&month=${month}`);
    }
  };

  // Naviga alla destinazione contestuale del feed Attività Recenti
  const handleActivityClick = (ev) => {
    if (ev.kind === "password_reset") {
      navigate("/impostazioni");
    } else if (ev.kind === "safety_expiring" || ev.kind === "safety_expired") {
      const empId = ev.payload?.employee_id || ev.actor_employee_id;
      if (empId) navigate(`/dipendenti/${empId}?tab=sicurezza`);
      else navigate("/scadenze-81");
    } else if (ev.employee_id && ev.year && ev.month) {
      // Presenza sent/approved/rejected O chiusura mese → griglia con filtro pre-impostato
      navigate(`/presenze?employee=${ev.employee_id}&year=${ev.year}&month=${ev.month}`);
    }
  };

  // First-run wizard for admin
  useEffect(() => {
    if (["admin", "super_admin"].includes(effectiveRole)) {
      (async () => {
        try {
          const { data } = await api.get("/companies");
          if (!data || data.length === 0) navigate("/wizard");
          else setCompanies(sortCompaniesIt(data));
        } catch (e) {
          toast.error(apiErr(e));
        }
      })();
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (companyId) params.company_id = companyId;
      const { data } = await api.get("/dashboard/summary", { params });
      setSummary(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
    // Exceptions summary (opzionale, solo se c'è un'azienda selezionata)
    if (companyId) {
      try {
        const { data } = await api.get("/guided/exceptions-summary", {
          params: { company_id: companyId, year, month },
        });
        setExcSummary(data);
      } catch (_) {
        setExcSummary(null);
      }
    } else {
      setExcSummary(null);
    }
    // Attività recenti (feed operativo suite)
    try {
      const { data } = await api.get("/dashboard/recent-activity", { params: { limit: 15, company_id: companyId || undefined } });
      setRecentEvents(data.events || []);
    } catch (_) {
      setRecentEvents([]);
    }
    // Notifiche (bell) — le mostriamo anche come widget nel middle
    try {
      const { data } = await api.get("/notifications", { params: { limit: 6 } });
      setNotifications(Array.isArray(data) ? data : (data?.items || []));
    } catch (_) {
      setNotifications([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, year, month]);
  useEffect(() => {
    announceShellCompany(
      user?.company_name || companyNameForShell(companies, companyId),
      companyId,
    );
  }, [companies, companyId, user?.company_name]);


  const chartData = (summary?.per_employee || []).map((r) => ({
    name: r.name.length > 14 ? r.name.slice(0, 12) + "…" : r.name,
    saldo: Number(r.progressive_balance?.toFixed(2)) || 0,
  }));

  // v0.99.30-c2 · Se il contesto azienda selezionato non gestisce banca ore,
  // il backend imposta summary.bank_hours_enabled=false. Il frontend nasconde
  // KPI saldo, grafico e tabella saldi. Default true (backward compat).
  const bankHoursOn = summary?.bank_hours_enabled !== false;
  const focusMonthCard = excSummary && companyId ? (
    <SuiteCard
      eyebrow="Focus del mese"
      eyebrowIcon={Sparkles}
      title="Il sistema gestisce automaticamente ordinario e festività. Concentrati sulle eccezioni."
      padding="lg"
      className="border-suite-primary/30 bg-gradient-to-br from-suite-primary-light/40 to-white"
      testid="focus-eccezioni-card"
      actions={
        <SuiteButton size="sm" onClick={() => navigate("/guidata")}
                iconRight={ArrowRight} data-testid="dashboard-guided-btn">
          Vai ad Assenze e Permessi
        </SuiteButton>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button type="button" onClick={() => setFocusFilter("regular")} data-testid="focus-regular-btn"
                className="bg-white rounded-md border border-suite-primary/30 p-3 text-left hover:shadow-md hover:border-suite-primary transition-all cursor-pointer">
          <div className="text-xs uppercase tracking-widest text-suite-primary font-semibold flex items-center gap-1"><CheckCircle2 size={11} /> Regolari</div>
          <div className="text-2xl font-bold tabular-nums text-suite-primary mt-1">{excSummary.n_regular}<span className="text-sm text-suite-neutral-400"> / {excSummary.n_employees}</span></div>
        </button>
        <button type="button" onClick={() => setFocusFilter("with_exc")} data-testid="focus-with-exc-btn"
                className="bg-white rounded-md border border-suite-warning/40 p-3 text-left hover:shadow-md hover:border-suite-warning transition-all cursor-pointer">
          <div className="text-xs uppercase tracking-widest text-suite-warning font-semibold">Con eccezioni</div>
          <div className="text-2xl font-bold tabular-nums text-suite-warning mt-1">{excSummary.n_with_exceptions}</div>
        </button>
        <button type="button" onClick={() => setFocusFilter("ferie")} data-testid="focus-ferie-btn"
                className="bg-white rounded-md border border-suite-warning/40 p-3 text-left hover:shadow-md hover:border-suite-warning transition-all cursor-pointer">
          <div className="text-xs uppercase tracking-widest text-suite-warning font-semibold flex items-center gap-1"><Palmtree size={11} /> Ferie</div>
          <div className="text-2xl font-bold tabular-nums text-suite-warning mt-1">{fmtHours(excSummary.totals.ferie_h)}<span className="text-sm">h</span></div>
        </button>
        <button type="button" onClick={() => setFocusFilter("permessi")} data-testid="focus-permessi-btn"
                className="bg-white rounded-md border border-suite-warning/40 p-3 text-left hover:shadow-md hover:border-suite-warning transition-all cursor-pointer">
          <div className="text-xs uppercase tracking-widest text-suite-warning font-semibold flex items-center gap-1"><Clock size={11} /> Permessi</div>
          <div className="text-2xl font-bold tabular-nums text-suite-warning mt-1">{fmtHours(excSummary.totals.permessi_h)}<span className="text-sm">h</span></div>
        </button>
        <button type="button" onClick={() => setFocusFilter("malattia")} data-testid="focus-malattia-btn"
                className="bg-white rounded-md border border-suite-error/40 p-3 text-left hover:shadow-md hover:border-suite-error transition-all cursor-pointer">
          <div className="text-xs uppercase tracking-widest text-suite-error font-semibold flex items-center gap-1"><HeartPulse size={11} /> Malattia</div>
          <div className="text-2xl font-bold tabular-nums text-suite-error mt-1">{fmtHours(excSummary.totals.malattia_h)}<span className="text-sm">h</span></div>
        </button>
      </div>
    </SuiteCard>
  ) : null;

  // ==========================================================
  // SPRINT V2 · Dashboard Enterprise (fedele a /mockups/dashboard-v1.html)
  // Struttura: Oggi devi fare / Stato organizzazione / Analisi.
  // Nessuna modifica a API/backend/RBAC/routing: solo layout.
  // ==========================================================
  const negBanca = (summary?.per_employee || [])
    .filter((r) => (r.progressive_balance ?? 0) < 0 && r.bank_hours_enabled !== false)
    .sort((a, b) => (a.progressive_balance ?? 0) - (b.progressive_balance ?? 0))
    .slice(0, 3);
  const chartDataFull = (summary?.per_employee || [])
    .filter((r) => r.bank_hours_enabled !== false)
    .map((r) => ({
      name: r.name.length > 14 ? r.name.slice(0, 12) + "…" : r.name,
      saldo: Number(r.progressive_balance?.toFixed(2)) || 0,
    }));

  const initials = (fullName) => (fullName || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");

  const monthLabel = `${MONTHS_IT[month - 1]} ${year}`;
  const companyName = user?.company_name || companyNameForShell(companies, companyId) || "Tutte le aziende";
  const firstName = (user?.name || "").split(/\s+/)[0] || "Paola";

  const anomCount = summary?.anomalies_count ?? 0;
  const pendingCount = summary?.attendance_pending ?? 0;
  const bankSum = summary?.total_progressive_balance ?? 0;

  return (
    <div
      className="space-y-0 max-w-[1400px] 3xl:max-w-[1720px] 4xl:max-w-[2200px] pb-24 lg:pb-0"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="dashboard-v2-root"
    >
      {/* ======================= HEADER PAGINA ======================= */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8"
      >
        <div>
          <div
            className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#2E7D32]"
            style={{ fontFamily: "Manrope" }}
            data-testid="dashboard-eyebrow"
          >
            Cruscotto · {monthLabel}
          </div>
          <h1
            className="text-[24px] sm:text-[28px] lg:text-[30px] leading-[1.15] mt-2 text-suite-neutral-900 font-bold tracking-tight"
            style={{ fontFamily: "Manrope", letterSpacing: "-0.01em" }}
            data-testid="dashboard-title"
          >
            Buongiorno {firstName}.<br />
            Ecco cosa richiede la tua attenzione oggi.
          </h1>
        </div>

        <div className="flex items-end gap-3">
          {["admin", "super_admin"].includes(effectiveRole) && (
            <div className="flex flex-col items-start lg:items-end">
              <div
                className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-suite-neutral-500 mb-1"
                style={{ fontFamily: "Manrope" }}
              >
                Azienda
              </div>
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger
                  className="h-9 min-w-[220px] rounded-lg border-suite-neutral-200 bg-white text-[13px] font-semibold text-suite-neutral-900 shadow-sm"
                  data-testid="dashboard-company-select"
                >
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex flex-col items-start lg:items-end">
            <div
              className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-suite-neutral-500 mb-1"
              style={{ fontFamily: "Manrope" }}
            >
              Periodo
            </div>
            <SuiteMonthPicker
              year={year}
              month={month}
              testid="dashboard-month-picker"
              onChange={({ year: nextYear, month: nextMonth }) => {
                setYear(nextYear);
                setMonth(nextMonth);
              }}
            />
          </div>
        </div>
      </motion.div>

      {/* ==================== BLOCCO 1 · OGGI DEVI FARE ==================== */}
      {effectiveRole !== "employee" && (
        <section className="mt-8 lg:mt-10" data-testid="dashboard-block-today">
          <div className="flex items-baseline justify-between mb-4 lg:mb-5">
            <h2
              className="text-[13px] lg:text-[15px] font-bold uppercase tracking-[0.16em] text-suite-neutral-900"
              style={{ fontFamily: "Manrope" }}
            >
              Oggi devi fare
            </h2>
            <span className="text-[11.5px] text-suite-neutral-500">Le priorità del giorno</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <PriorityCard
              icon={ClipboardList}
              badge="Priorità"
              tone="primary"
              value={pendingCount}
              title="Cartellini da validare"
              subtitle={
                loading
                  ? "Caricamento…"
                  : pendingCount === 0
                    ? "Nessun cartellino in attesa oggi"
                    : "Inviati dai dipendenti · pronti per revisione"
              }
              cta="Apri i cartellini"
              onClick={() => pendingCount > 0 && setKpiDetail("pending")}
              testid="today-cartellini"
            />

            <LeaveRequestsPriorityCard testid="today-leave" />

            <PriorityCard
              icon={AlertTriangle}
              badge="Attenzione"
              tone="warn"
              value={anomCount}
              title="Anomalie da controllare"
              subtitle={
                loading
                  ? "Caricamento…"
                  : anomCount === 0
                    ? "Nessuna anomalia rilevata sul mese"
                    : "Presenze o saldi con incongruenze"
              }
              cta="Vedi le anomalie"
              onClick={() => anomCount > 0 && setKpiDetail("anomalies")}
              testid="today-anomalie"
            />

            <SafetyPriorityCard
              companyId={companyId}
              onOpen={() => navigate("/scadenze-81")}
              testid="today-scadenze"
            />
          </div>
        </section>
      )}

      {/* ==================== BLOCCO 2 · STATO ORGANIZZAZIONE ==================== */}
      <section className="mt-10 lg:mt-14" data-testid="dashboard-block-state">
        <div className="flex items-baseline justify-between mb-4 lg:mb-5">
          <h2
            className="text-[13px] lg:text-[15px] font-bold uppercase tracking-[0.16em] text-suite-neutral-900"
            style={{ fontFamily: "Manrope" }}
          >
            Stato della tua organizzazione
          </h2>
          <span className="hidden sm:inline text-[11.5px] text-suite-neutral-500">
            Aggiornato ora · {companyName}
          </span>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
          <StateTile
            label="Dipendenti attivi"
            value={summary?.active_employees_count ?? "—"}
            sub={`su ${summary?.employees_count ?? 0} totali`}
            trend="up"
            onClick={() => setKpiDetail("employees")}
            testid="state-employees"
          />
          {bankHoursOn && (
            <StateTile
              label="Saldo banca ore"
              value={fmtHoursSigned(bankSum)}
              sub="ore progressivo azienda"
              negative={bankSum < 0}
              trend={bankSum < 0 ? "down" : "up"}
              onClick={() => setKpiDetail("balance")}
              testid="state-balance"
            />
          )}
          <StateTile
            label="Importazioni del mese"
            value={summary?.imports_count ?? 0}
            sub="cartellini caricati"
            trend="up"
            onClick={() => setKpiDetail("imports")}
            testid="state-imports"
          />
          <StateTile
            label="Mesi chiusi"
            value={summary?.closed_months_count ?? 0}
            sub={`nell'anno ${year}`}
            year={year}
            monthsClosed={summary?.closed_months_count ?? 0}
            onClick={() => setKpiDetail("closed")}
            testid="state-closed"
          />
        </div>
      </section>

      {/* ==================== BLOCCO 3 · ANALISI ==================== */}
      {bankHoursOn && (
        <section className="mt-10 lg:mt-14 mb-10 lg:mb-16" data-testid="dashboard-block-analysis">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-4 lg:mb-5 gap-2">
            <h2
              className="text-[13px] lg:text-[15px] font-bold uppercase tracking-[0.16em] text-suite-neutral-900"
              style={{ fontFamily: "Manrope" }}
            >
              Analisi
            </h2>
            <div className="flex items-center gap-4 text-[11.5px] lg:text-[12px] text-suite-neutral-500">
              <span className="text-suite-neutral-700 font-semibold border-b-2 border-[#2E7D32] pb-0.5">
                Saldo banca ore
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* Grafico */}
            <div
              className="xl:col-span-2 rounded-2xl bg-white border border-suite-neutral-200 p-5 lg:p-6"
              style={{ boxShadow: "0 1px 0 rgba(15,23,42,0.04), 0 12px 40px -24px rgba(15,23,42,0.14)" }}
              data-testid="analysis-chart-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-2">
                <div>
                  <div
                    className="text-[13.5px] font-bold text-suite-neutral-900"
                    style={{ fontFamily: "Manrope" }}
                  >
                    Saldo banca ore progressivo · per dipendente
                  </div>
                  <div className="text-[11.5px] text-suite-neutral-500 mt-0.5">
                    {monthLabel} · {companyName}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-suite-neutral-500">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#2E7D32]" />Positivo</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#B45309]" />Negativo</span>
                </div>
              </div>
              {chartDataFull.length === 0 ? (
                <div className="h-[220px] lg:h-[240px] flex items-center justify-center text-sm text-suite-neutral-400">
                  {loading ? "Caricamento del grafico…" : "Nessun dato per il periodo selezionato"}
                </div>
              ) : (
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartDataFull} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                      <Tooltip formatter={(v) => `${v} h`} />
                      <ReferenceLine y={0} stroke="#334155" />
                      <Bar dataKey="saldo" radius={[4, 4, 0, 0]}>
                        {chartDataFull.map((entry, i) => (
                          <Cell key={i} fill={entry.saldo >= 0 ? "#2E7D32" : "#B45309"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Card laterale: Attenzione a (top 3 negativi) */}
            <div
              className="rounded-2xl bg-white border border-suite-neutral-200 p-5 lg:p-6"
              style={{ boxShadow: "0 1px 0 rgba(15,23,42,0.04), 0 12px 40px -24px rgba(15,23,42,0.14)" }}
              data-testid="analysis-attention-card"
            >
              <div
                className="text-[13.5px] font-bold text-suite-neutral-900"
                style={{ fontFamily: "Manrope" }}
              >
                Attenzione a
              </div>
              <div className="text-[11.5px] text-suite-neutral-500 mt-0.5 mb-4">
                Dipendenti con saldo negativo persistente
              </div>

              {negBanca.length === 0 ? (
                <div className="py-10 text-center">
                  <CheckCircle2 size={26} className="mx-auto text-[#2E7D32] mb-2" />
                  <div className="text-sm text-suite-neutral-500">
                    Nessun saldo negativo da presidiare.
                  </div>
                </div>
              ) : (
                <ul className="space-y-3">
                  {negBanca.map((r) => (
                    <li
                      key={r.employee_id}
                      className="flex items-center gap-3 cursor-pointer hover:bg-suite-surface-hover -mx-1 px-1 py-1 rounded-md transition-colors"
                      onClick={() => navigate(`/presenze?employee=${r.employee_id}&year=${year}&month=${month}`)}
                      data-testid={`attention-row-${r.employee_id}`}
                    >
                      <div className="h-9 w-9 rounded-full bg-[#FEF3C7] text-[#B45309] text-[11px] font-bold flex items-center justify-center">
                        {initials(r.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-suite-neutral-900 truncate">{r.name}</div>
                        <div className="text-[11px] text-suite-neutral-500">Saldo mese · progressivo</div>
                      </div>
                      <div className="text-right">
                        <div
                          className="text-[13px] font-bold text-[#B45309]"
                          style={{ fontFamily: "JetBrains Mono", fontVariantNumeric: "tabular-nums" }}
                        >
                          {fmtHoursSigned(r.progressive_balance)}
                        </div>
                        <div className="text-[10.5px] text-suite-neutral-500">progressivo</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                onClick={() => navigate("/report")}
                className="mt-5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#1B5E20] hover:gap-2 transition-all"
                data-testid="attention-open-report"
              >
                Apri il report banca ore
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ==================== FOCUS ECCEZIONI (senza banca ore) ==================== */}
      {!bankHoursOn && focusMonthCard}

      {/* ==================== DETAIL DIALOGS (invariati) ==================== */}
      <Dialog open={!!kpiDetail} onOpenChange={(open) => !open && setKpiDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="kpi-detail-dialog">
          <DialogHeader>
            <DialogTitle>{kpiList.title}</DialogTitle>
            <DialogDescription>{kpiList.description}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {kpiList.rows.length === 0 ? (
              <div className="text-center py-8 text-suite-neutral-500">
                Nessun elemento nel periodo selezionato
              </div>
            ) : (
              <ul className="divide-y divide-suite-neutral-200">
                {kpiList.rows.map((row, index) => (
                  <li
                    key={row.employee_id || row.id || (row.year && row.month ? `${row.year}-${row.month}` : `row-${index}`)}
                    onClick={() => openKpiRow(row)}
                    className="p-3 flex items-center justify-between gap-4 hover:bg-suite-surface-hover cursor-pointer"
                    data-testid={`kpi-detail-row-${index}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-suite-neutral-800">{row.primary}</div>
                      <div className="text-xs text-suite-neutral-500 mt-0.5">{row.secondary}</div>
                    </div>
                    <ArrowRight size={16} className="text-suite-neutral-400 flex-shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!focusFilter} onOpenChange={(v) => !v && setFocusFilter(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="focus-dialog">
          <DialogHeader>
            <DialogTitle>{focusList.title}</DialogTitle>
            <DialogDescription>{focusList.rows.length} dipendenti — clic sul nome per aprire la sua Gestione Presenze</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {focusList.rows.length === 0 ? (
              <div className="text-center py-8 text-suite-neutral-500">Nessun dipendente in questa categoria</div>
            ) : (
              <ul className="divide-y divide-suite-neutral-200">
                {focusList.rows.map((e, i) => (
                  <li
                    key={e.employee_id || i}
                    onClick={() => { setFocusFilter(null); navigate(`/presenze?employee=${e.employee_id}&year=${year}&month=${month}`); }}
                    className="p-3 flex items-center justify-between hover:bg-suite-surface-hover cursor-pointer"
                    data-testid={`focus-row-${e.employee_id || i}`}
                  >
                    <div>
                      <div className="text-sm font-semibold text-suite-neutral-800">
                        {e.last_name || e.first_name
                          ? `${e.last_name || ""} ${e.first_name || ""}`.trim()
                          : e.employee_name}
                      </div>
                      {e.matricola && <div className="text-[10px] font-mono text-suite-neutral-500">Matr. {e.matricola}</div>}
                    </div>
                    <div className="flex gap-2 text-xs">
                      {(e.ferie_h || 0) > 0 && <Badge variant="outline" className="bg-suite-warning-light border-suite-warning/40 text-suite-warning">Ferie {fmtHours(e.ferie_h)}h</Badge>}
                      {(e.permessi_h || 0) > 0 && <Badge variant="outline" className="bg-suite-warning-light border-suite-warning/40 text-suite-warning">Perm. {fmtHours(e.permessi_h)}h</Badge>}
                      {(e.malattia_h || 0) > 0 && <Badge variant="outline" className="bg-suite-error-light border-suite-error/40 text-suite-error">Mal. {fmtHours(e.malattia_h)}h</Badge>}
                      {!e.has_exc && <Badge variant="outline" className="bg-suite-primary-light border-suite-primary-border text-suite-primary">Regolare</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================================================================
 * SPRINT V2 · Presentational components — solo layout, zero side-effect.
 * Vivono in Dashboard.jsx per non toccare `components/suite/` (usati
 * anche da altre pagine e classificati Shared Product).
 * =================================================================== */

const TONE_MAP = {
  primary: {
    icon: "bg-[#E8F3E9] text-[#1B5E20]",
    badge: "bg-[#E8F3E9] text-[#1B5E20]",
    border: "border-suite-neutral-200 hover:border-[#2E7D32]",
    valueColor: "text-suite-neutral-900",
    cta: "text-[#1B5E20]",
  },
  info: {
    icon: "bg-[#DBEAFE] text-[#1D4ED8]",
    badge: "bg-[#DBEAFE] text-[#1D4ED8]",
    border: "border-suite-neutral-200 hover:border-[#2E7D32]",
    valueColor: "text-suite-neutral-900",
    cta: "text-[#1B5E20]",
  },
  warn: {
    icon: "bg-[#FEF3C7] text-[#B45309]",
    badge: "bg-[#FEF3C7] text-[#B45309]",
    border: "border-[#B45309]/40 hover:border-[#B45309]",
    valueColor: "text-[#B45309]",
    cta: "text-[#B45309]",
  },
};

function PriorityCard({ icon: Icon, badge, tone = "primary", value, title, subtitle, cta, onClick, testid }) {
  const t = TONE_MAP[tone] || TONE_MAP.primary;
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      className={`group text-left rounded-2xl bg-white border ${t.border} p-5 lg:p-6 flex flex-col justify-between min-h-[180px] lg:min-h-[190px] transition-all ${clickable ? "hover:-translate-y-0.5 hover:shadow-lg" : ""}`}
      style={{ boxShadow: "0 1px 0 rgba(15,23,42,0.04), 0 12px 40px -24px rgba(15,23,42,0.14)" }}
      data-testid={testid}
      aria-label={`${title} · ${value}`}
    >
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.icon}`}>
          <Icon size={20} strokeWidth={2} />
        </div>
        <span
          className={`text-[10.5px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full ${t.badge}`}
          style={{ fontFamily: "Manrope" }}
        >
          {badge}
        </span>
      </div>
      <div>
        <div
          className={`text-[34px] lg:text-[36px] font-bold leading-none ${t.valueColor}`}
          style={{ fontFamily: "JetBrains Mono", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}
        >
          {value}
        </div>
        <div className="text-[14px] font-semibold text-suite-neutral-900 mt-2" style={{ fontFamily: "Manrope" }}>
          {title}
        </div>
        <div className="text-[12.5px] text-suite-neutral-500 mt-1 leading-snug">{subtitle}</div>
      </div>
      {cta && (
        <div className={`text-[12.5px] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all ${t.cta}`}>
          {cta}
          <ArrowRight size={14} />
        </div>
      )}
    </button>
  );
}

function StateTile({ label, value, sub, negative, trend, year: currentYear, monthsClosed, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl bg-white border border-suite-neutral-200 p-4 lg:p-5 hover:border-[#2E7D32] transition-colors"
      data-testid={testid}
      aria-label={`${label} · ${value}`}
    >
      <div
        className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-suite-neutral-500"
        style={{ fontFamily: "Manrope" }}
      >
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div
          className={`text-[24px] lg:text-[26px] font-bold leading-none ${negative ? "text-[#B45309]" : "text-suite-neutral-900"}`}
          style={{ fontFamily: "JetBrains Mono", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}
        >
          {value}
        </div>
        <div className="text-[11px] lg:text-[11.5px] text-suite-neutral-500">{sub}</div>
      </div>
      <div className="mt-4 h-8">
        {typeof monthsClosed === "number" && currentYear ? (
          <div className="flex items-center gap-[3px] h-8">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className={`flex-1 h-1.5 rounded-sm ${
                  i < monthsClosed
                    ? "bg-[#2E7D32]"
                    : i === monthsClosed
                      ? "bg-[#E8F3E9]"
                      : "bg-suite-neutral-200"
                }`}
              />
            ))}
          </div>
        ) : (
          <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none">
            <polyline
              fill="none"
              stroke={negative ? "#B45309" : "#2E7D32"}
              strokeWidth="1.6"
              points={
                trend === "down"
                  ? "0,10 15,12 30,14 45,18 60,20 75,22 90,24 100,26"
                  : "0,26 15,24 30,22 45,18 60,16 75,14 90,12 100,10"
              }
            />
          </svg>
        )}
      </div>
    </button>
  );
}

/* Card priorità Richieste ferie — usa lo stesso endpoint della widget storica. */
function LeaveRequestsPriorityCard({ testid }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/leave-requests", { params: { status: "pending" } });
        if (mounted) setItems(Array.isArray(data) ? data : []);
      } catch (_) { if (mounted) setItems([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const count = items.length;
  const nearest = React.useMemo(() => {
    if (!items.length) return null;
    const sorted = [...items].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    return sorted[0];
  }, [items]);

  let subtitle = "Caricamento…";
  if (!loading) {
    if (count === 0) subtitle = "Nessuna richiesta in attesa · nessuno aspetta te";
    else if (nearest) {
      const dt = nearest.start_date ? new Date(nearest.start_date) : null;
      const dLabel = dt ? dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" }) : "";
      const name = nearest._employee_name || nearest.employee_id;
      subtitle = `La più vicina: ${dLabel} · ${name}`;
    } else {
      subtitle = "Richieste in attesa di approvazione";
    }
  }

  return (
    <PriorityCard
      icon={Activity}
      badge="Da approvare"
      tone="info"
      value={loading ? "…" : count}
      title="Richieste in attesa"
      subtitle={subtitle}
      cta="Apri l'inbox"
      onClick={() => navigate("/richieste-ferie")}
      testid={testid}
    />
  );
}

/* Card priorità Scadenze 81/2008 — usa lo stesso endpoint /safety/kpis. */
function SafetyPriorityCard({ companyId, onOpen, testid }) {
  const [kpis, setKpis] = React.useState({ total: 0, by_status: {} });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const params = companyId ? { company_id: companyId } : {};
        const { data } = await api.get("/safety/kpis", { params });
        if (mounted) {
          setKpis(
            data && typeof data === "object" && !Array.isArray(data)
              ? data
              : { total: 0, by_status: {} }
          );
        }
      } catch (_) { if (mounted) setKpis({ total: 0, by_status: {} }); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [companyId]);

  const scad = kpis.by_status?.scaduta || 0;
  const inScad = kpis.by_status?.in_scadenza || 0;
  const total = scad + inScad;

  return (
    <PriorityCard
      icon={CalendarCheck}
      badge="Entro 30 gg"
      tone="primary"
      value={loading ? "…" : total}
      title="Scadenze in avvicinamento"
      subtitle={
        loading
          ? "Caricamento…"
          : total === 0
            ? "Nessuna scadenza D.Lgs. 81/2008 imminente"
            : `${scad} scadut${scad === 1 ? "a" : "e"} · ${inScad} in scadenza`
      }
      cta="Apri il modulo Sicurezza"
      onClick={onOpen}
      testid={testid}
    />
  );
}

/** Widget "Richieste da approvare" per admin/client (Dashboard). */
function LeaveRequestsWidget() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/leave-requests", { params: { status: "pending" } });
      setItems(Array.isArray(data) ? data : []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/leave-requests/${id}/approve`, {}); toast.success("Richiesta approvata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reject = async (id) => {
    const reason = prompt("Motivo del rifiuto?");
    if (reason === null) return;
    try { await api.post(`/leave-requests/${id}/reject`, { reason }); toast.success("Richiesta respinta"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <SuiteCard
      eyebrow="Richieste ferie/permesso"
      eyebrowIcon={ClipboardList}
      eyebrowTone="warning"
      title={`${items.length} da approvare`}
      padding="none"
      testid="leave-requests-widget"
      className="min-h-[150px]"
    >
      {loading ? (
        <div className="p-6 text-sm text-suite-neutral-400 text-center">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="p-4 text-center">
          <CheckCircle2 size={26} className="mx-auto text-suite-accent mb-2" />
          <div className="text-sm text-suite-neutral-500">Nessuna richiesta in attesa</div>
        </div>
      ) : (
        <ul className="divide-y divide-suite-neutral-100 max-h-[420px] overflow-y-auto">
          {items.map((lr) => (
            <li key={lr.id} className="p-3 flex items-start justify-between gap-3 hover:bg-suite-surface-hover" data-testid={`admin-leave-${lr.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-suite-neutral-800 truncate">{lr._employee_name || lr.employee_id}</div>
                <div className="text-xs text-suite-neutral-500 mt-0.5">
                  {lr.kind === "FERIE" ? "Ferie" : lr.kind === "PERMESSI" ? "Permessi (ROL)" : lr.kind === "EX_FESTIVITA" ? "Ex festività" : "Permessi studio"}
                  {" · "}dal {lr.start_date} al {lr.end_date}
                  {" · "}<span className="font-mono">{Number(lr.total_days || 0).toFixed(2).replace(".", ",")}</span> gg
                </div>
                {lr.notes && <div className="text-xs text-suite-neutral-600 italic mt-0.5">&quot;{lr.notes}&quot;</div>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => approve(lr.id)}
                        className="text-xs px-2 py-1 rounded bg-suite-accent hover:bg-suite-accent-hover text-white font-semibold transition-colors"
                        data-testid={`approve-${lr.id}`}>Approva</button>
                <button type="button" onClick={() => reject(lr.id)}
                        className="text-xs px-2 py-1 rounded bg-white hover:bg-suite-error-light text-suite-error border border-suite-error/40 font-semibold transition-colors"
                        data-testid={`reject-${lr.id}`}>Respingi</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SuiteCard>
  );
}

function SafetyUpcomingWidget({ companyId, navigate }) {
  const [items, setItems] = React.useState([]);
  const [kpis, setKpis] = React.useState({ total: 0, by_status: {} });
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const params = companyId ? { company_id: companyId } : {};
        const [u, k] = await Promise.all([
          api.get("/safety/upcoming", { params: { ...params, limit: 6 } }),
          api.get("/safety/kpis", { params }),
        ]);
        if (mounted) {
          // La release stabile può avere il modulo Sicurezza disattivato lato
          // backend. In quel caso il fallback SPA restituisce HTML anziché una
          // lista JSON: non deve mai bloccare l'intero Cruscotto.
          setItems(Array.isArray(u.data) ? u.data : []);
          setKpis(
            k.data && typeof k.data === "object" && !Array.isArray(k.data)
              ? k.data
              : { total: 0, by_status: {} }
          );
        }
      } catch { if (mounted) { setItems([]); setKpis({ total: 0, by_status: {} }); } }
      finally { if (mounted) setLoading(false); }
    }
    load();
    return () => { mounted = false; };
  }, [companyId]);

  const scad = kpis.by_status?.scaduta || 0;
  const inScad = kpis.by_status?.in_scadenza || 0;
  const hasAlerts = scad + inScad > 0;

  return (
    <SuiteCard
      eyebrow="Sicurezza sul lavoro"
      eyebrowIcon={ShieldCheck}
      title={hasAlerts
        ? `${scad + inScad} scadenza${scad + inScad === 1 ? "" : "e"} da presidiare`
        : "Nessuna scadenza imminente"}
      description="D.Lgs. 81/2008 · le prossime scadenze e quelle già scadute"
      action={
        <SuiteButton variant="ghost" size="sm"
          onClick={() => navigate("/scadenze-81")}
          icon={ArrowRight}
          data-testid="dashboard-safety-open">
          Apri modulo
        </SuiteButton>
      }
      testid="dashboard-safety-card"
      padding="sm"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <MiniStat label="Totale" value={kpis.total} tone="bg-slate-50 text-slate-700" />
        <MiniStat label="Regolari" value={kpis.by_status?.regolare || 0} tone="bg-emerald-50 text-emerald-700" />
        <MiniStat label="In scadenza" value={inScad} tone="bg-amber-50 text-amber-800" />
        <MiniStat label="Scadute" value={scad} tone="bg-red-50 text-red-700" />
      </div>
      {loading ? (
        <div className="text-sm text-slate-500 py-4 text-center">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">Tutte le scadenze sono in regola.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((r) => {
            const overdue = r.status === "scaduta";
            return (
              <li key={r.id}>
                <button type="button"
                  onClick={() => navigate(`/dipendenti/${r.employee.id}?tab=sicurezza`)}
                  className="w-full py-2 px-1 flex items-center gap-3 hover:bg-slate-50 rounded-md text-left"
                  data-testid={`dashboard-safety-row-${r.id}`}>
                  <div className={`w-2 h-2 rounded-full ${overdue ? "bg-red-500" : (r.severity === "red" ? "bg-red-400" : r.severity === "orange" ? "bg-amber-500" : "bg-yellow-500")}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {r.employee.last_name} {r.employee.first_name}
                      <span className="text-slate-400 font-normal"> · {r.category.name}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {overdue
                        ? <><XCircle size={11} className="inline-block mr-1 text-red-600" />Scaduta il {fmt(r.expires_on)} ({Math.abs(r.days_left)} giorni fa)</>
                        : <><AlertTriangle size={11} className="inline-block mr-1 text-amber-600" />Scade il {fmt(r.expires_on)} · tra {r.days_left} giorni</>}
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </SuiteCard>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className={`rounded-md px-2.5 py-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function fmt(iso) {
  return formatDateIT(iso, "—");
}
